import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Box,
} from '@mui/material';
import dayjs from 'dayjs';
import { atualizarPagamentoInscritosLiga } from '../../../services/pagamentoInscritosLigaService';
import type { PagamentoInscritosLiga, PagamentoInscritosLigaInput } from '../../../types/pagamentoInscritosLiga';
import type { StatusPagamento } from '../../../types/pagamento';

const STATUS_OPTIONS: { label: string; value: StatusPagamento }[] = [
  { label: 'Pendente', value: 'PENDENTE' },
  { label: 'Pago', value: 'PAGO' },
  { label: 'Cancelado', value: 'CANCELADO' },
  { label: 'Estornado', value: 'ESTORNADO' },
  { label: 'Gratuito', value: 'GRATUITO' },
];

const toDatetimeLocal = (value?: string | null): string => {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : '';
};

export interface AdminEditPagamentoDialogProps {
  open: boolean;
  pagamento: PagamentoInscritosLiga | null;
  onClose: () => void;
  onSaved: () => void;
}

const AdminEditPagamentoDialog: React.FC<AdminEditPagamentoDialogProps> = ({
  open,
  pagamento,
  onClose,
  onSaved,
}) => {
  const [status, setStatus] = useState<string>('');
  const [valor, setValor] = useState<string>('');
  const [dataVencimento, setDataVencimento] = useState<string>('');
  const [dataPagamento, setDataPagamento] = useState<string>('');
  const [codigoTransacao, setCodigoTransacao] = useState<string>('');
  const [reciboUrl, setReciboUrl] = useState<string>('');
  const [invoiceId, setInvoiceId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (pagamento) {
      setStatus(pagamento.status || '');
      setValor(String(pagamento.valor ?? ''));
      setDataVencimento(toDatetimeLocal(pagamento.dataVencimento));
      setDataPagamento(toDatetimeLocal(pagamento.dataPagamento));
      setCodigoTransacao(pagamento.codigoTransacao || '');
      setReciboUrl(pagamento.reciboUrl || '');
      setInvoiceId(pagamento.invoiceId || '');
    }
  }, [pagamento]);

  const handleSave = async () => {
    if (!pagamento) return;
    setSaving(true);
    try {
      const input: PagamentoInscritosLigaInput = {
        status: status || null,
        valor: valor ? Number(valor) : null,
        dataVencimento: dataVencimento ? dayjs(dataVencimento).toISOString() : null,
        dataPagamento: dataPagamento ? dayjs(dataPagamento).toISOString() : null,
        codigoTransacao: codigoTransacao || null,
        reciboUrl: reciboUrl || null,
        invoiceId: invoiceId || null,
      };
      await atualizarPagamentoInscritosLiga(pagamento.id, input);
      onSaved();
      onClose();
    } catch (e: any) {
      const msg =
        e?.graphQLErrors?.[0]?.message || e?.message || 'Erro ao atualizar pagamento.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Pagamento</DialogTitle>
        <DialogContent dividers>
          {pagamento && (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <TextField
                select
                label="Status"
                size="small"
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                InputLabelProps={{ shrink: true }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Valor"
                type="number"
                size="small"
                fullWidth
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: '0.01', min: '0' }}
              />

              <TextField
                label="Data Vencimento"
                type="datetime-local"
                size="small"
                fullWidth
                value={dataVencimento}
                onChange={(e) => setDataVencimento(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Data Pagamento"
                type="datetime-local"
                size="small"
                fullWidth
                value={dataPagamento}
                onChange={(e) => setDataPagamento(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Código Transação"
                size="small"
                fullWidth
                value={codigoTransacao}
                onChange={(e) => setCodigoTransacao(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Recibo URL"
                size="small"
                fullWidth
                value={reciboUrl}
                onChange={(e) => setReciboUrl(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Invoice ID"
                size="small"
                fullWidth
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminEditPagamentoDialog;
